import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { reportErrorBoundaryToSentry } from '@/lib/sentry-report-boundary'
import { CommandBarDeclarationProvider } from './context/CommandBarDeclarationContext'
import { CommandBarOverlay } from './CommandBarOverlay'
import { CommandBarModalsHost } from './modals/CommandBarModalsHost'
import { useCommandBarGlobalHotkey } from './hooks/useCommandBarGlobalHotkey'
import type { CommandBarModalId } from './types'

type CommandBarContextValue = {
  open: boolean
  openCommandBar: () => void
  closeCommandBar: () => void
  toggleCommandBar: () => void
  openModal: (modalId: CommandBarModalId) => void
}

const CommandBarContext = createContext<CommandBarContextValue | null>(null)

function CommandBarInner({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [activeModal, setActiveModal] = useState<CommandBarModalId | null>(null)

  const openCommandBar = useCallback(() => setOpen(true), [])
  const closeCommandBar = useCallback(() => setOpen(false), [])
  const toggleCommandBar = useCallback(() => setOpen((prev) => !prev), [])
  const openModal = useCallback((modalId: CommandBarModalId) => {
    setActiveModal(modalId)
  }, [])
  const closeModal = useCallback(() => setActiveModal(null), [])

  useCommandBarGlobalHotkey(toggleCommandBar)

  const value = useMemo(
    () => ({
      open,
      openCommandBar,
      closeCommandBar,
      toggleCommandBar,
      openModal,
    }),
    [closeCommandBar, open, openCommandBar, openModal, toggleCommandBar]
  )

  return (
    <CommandBarContext.Provider value={value}>
      {children}
      <ErrorBoundary
        fallbackRender={() => null}
        onError={reportErrorBoundaryToSentry}
      >
        <CommandBarOverlay
          open={open}
          onClose={closeCommandBar}
          openModal={openModal}
        />
      </ErrorBoundary>
      <CommandBarModalsHost activeModal={activeModal} onCloseModal={closeModal} />
    </CommandBarContext.Provider>
  )
}

export function CommandBarProvider({ children }: { children: ReactNode }) {
  return (
    <CommandBarDeclarationProvider>
      <CommandBarInner>{children}</CommandBarInner>
    </CommandBarDeclarationProvider>
  )
}

export function useCommandBar() {
  const context = useContext(CommandBarContext)
  if (!context) {
    throw new Error('useCommandBar must be used within CommandBarProvider')
  }
  return context
}
